import { Alert, Box, CircularProgress, Divider, Stack } from "@mui/material";
import { Text } from "@src/lib/ui-react";

import { DynamicSchemaForm } from "../DynamicSchemaForm";
import { EvidenceDefinitionSelect } from "../EvidenceDefinitionSelect";
import { type EvidenceFormFieldProps } from "./useEvidenceForm";

export interface EvidenceFormViewProps extends EvidenceFormFieldProps {
  isLoading: boolean;
  isError: boolean;
}

export function EvidenceFormView(props: EvidenceFormViewProps) {
  const {
    control,
    errors,
    definitions,
    selectedDefinitionId,
    selectedDefinition,
    propertySchema,
    hasProperties,
    onDefinitionChange,
    isLoading,
    isError,
    isDefinitionMissing,
  } = props;

  if (isLoading) {
    return (
      <>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          Failed to load evidence definitions. Evidence properties cannot be edited.
        </Alert>
      </>
    );
  }

  if (isDefinitionMissing) {
    return (
      <>
        <Divider />
        <Alert severity="warning" sx={{ marginTop: 1 }}>
          Evidence definition not found. Evidence properties cannot be edited.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Divider />
      <Text bold variant="subtitle1">
        Evidence
      </Text>

      <Box sx={{ marginTop: 2, marginBottom: 2 }}>
        <EvidenceDefinitionSelect
          definitions={definitions}
          isLoading={false}
          selectedDefinitionId={selectedDefinitionId}
          onSelect={onDefinitionChange}
        />
      </Box>

      {selectedDefinition && propertySchema && hasProperties && (
        <Stack spacing={2} sx={{ marginBottom: 2 }}>
          <DynamicSchemaForm
            control={control}
            errors={errors}
            pathPrefix=""
            schema={propertySchema}
          />
        </Stack>
      )}

      {selectedDefinition && propertySchema && !hasProperties && (
        <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="body2">
          This definition has no properties to fill in.
        </Text>
      )}
    </>
  );
}
